import { CRM_UNASSIGNED_ASSIGNEE_FILTER_VALUE } from "@ease/crm/types";

interface ContactFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  tag: string;
  onTagChange: (value: string) => void;
  minScore: string;
  onMinScoreChange: (value: string) => void;
  maxScore: string;
  onMaxScoreChange: (value: string) => void;
  createdFrom: string;
  onCreatedFromChange: (value: string) => void;
  createdTo: string;
  onCreatedToChange: (value: string) => void;
  lifecycleStage: string;
  onLifecycleStageChange: (value: string) => void;
  source: string;
  onSourceChange: (value: string) => void;
  sourceOptions: string[];
  isSourceOptionsLoading: boolean;
  sourceOptionsError: string | null;
  assignedTo: string;
  onAssignedToChange: (value: string) => void;
  ownerOptions: Array<{ id: string; label: string }>;
  areOwnerOptionsLoading: boolean;
  ownerOptionsError: string | null;
  onCreateContact: () => void;
}

/** Filter bar for the contact list — search input + tag filter + create button. */
export function ContactFilters({
  search,
  onSearchChange,
  tag,
  onTagChange,
  minScore,
  onMinScoreChange,
  maxScore,
  onMaxScoreChange,
  createdFrom,
  onCreatedFromChange,
  createdTo,
  onCreatedToChange,
  lifecycleStage,
  onLifecycleStageChange,
  source,
  onSourceChange,
  sourceOptions,
  isSourceOptionsLoading,
  sourceOptionsError,
  assignedTo,
  onAssignedToChange,
  ownerOptions,
  areOwnerOptionsLoading,
  ownerOptionsError,
  onCreateContact,
}: ContactFiltersProps) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]/70 p-4 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-1 flex-col gap-2 sm:flex-row">
          <div className="relative flex-1 sm:max-w-sm">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">🔍</span>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search contacts..."
            className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>

          <input
            type="text"
            value={tag}
            onChange={(e) => onTagChange(e.target.value)}
            placeholder="Filter by tag..."
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground sm:w-44"
          />
        </div>

        <button
          onClick={onCreateContact}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          + Add Contact
        </button>
      </div>

      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-7">
        <input
          type="number"
          min="0"
          max="100"
          value={minScore}
          onChange={(e) => onMinScoreChange(e.target.value)}
          placeholder="Min score"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground"
        />
        <input
          type="number"
          min="0"
          max="100"
          value={maxScore}
          onChange={(e) => onMaxScoreChange(e.target.value)}
          placeholder="Max score"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground"
        />
        <input
          aria-label="Created from"
          type="date"
          value={createdFrom}
          onChange={(e) => onCreatedFromChange(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        />
        <input
          aria-label="Created to"
          type="date"
          value={createdTo}
          onChange={(e) => onCreatedToChange(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        />
        <select
          aria-label="Lifecycle stage"
          value={lifecycleStage}
          onChange={(e) => onLifecycleStageChange(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        >
          <option value="">All stages</option>
          <option value="lead">Lead</option>
          <option value="mql">MQL</option>
          <option value="sql">SQL</option>
          <option value="customer">Customer</option>
          <option value="churned">Churned</option>
        </select>
        <select
          aria-label="Source"
          value={source}
          onChange={(e) => onSourceChange(e.target.value)}
          disabled={isSourceOptionsLoading || Boolean(sourceOptionsError)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        >
          <option value="">
            {isSourceOptionsLoading
              ? "Loading sources..."
              : sourceOptionsError
                ? "Source list unavailable"
                : "All sources"}
          </option>
          {sourceOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <select
          aria-label="Owner"
          value={assignedTo}
          onChange={(e) => onAssignedToChange(e.target.value)}
          disabled={areOwnerOptionsLoading || Boolean(ownerOptionsError)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        >
          <option value="">
            {areOwnerOptionsLoading
              ? "Loading owners..."
              : ownerOptionsError
                ? "Owner list unavailable"
                : "All owners"}
          </option>
          <option value={CRM_UNASSIGNED_ASSIGNEE_FILTER_VALUE}>Unassigned</option>
          {ownerOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {(sourceOptionsError || ownerOptionsError) && (
        <p className="text-xs text-[var(--color-destructive)]">
          {sourceOptionsError && ownerOptionsError
            ? "Source and owner filtering are temporarily unavailable. The rest of the contact filters still work."
            : sourceOptionsError
              ? "Source filtering is temporarily unavailable. The rest of the contact filters still work."
              : "Owner filtering is temporarily unavailable. The rest of the contact filters still work."}
        </p>
      )}
    </div>
  );
}
